import * as React from 'react';
import { createContext, useContext, useState } from 'react';

import type { SimilarityChecksData } from 'bundles/academic-integrity/components/plagiarism-detection/SimilarityChecks';

export type SimilarityChecksDataMapType = Record<string, SimilarityChecksData | undefined>;

// `SubmitForCheckErrorMapType` with an objectId as key and networkErrorCode as value
export type SubmitForCheckErrorMapType = Record<string, string | undefined>;
export type HasContentMapType = Record<string, boolean>;

export type PlagiarismDetectionContextType = {
  isReadyForCheck: boolean;
  setIsReadyForCheck: React.Dispatch<React.SetStateAction<boolean>>;
  isPollingTimedOut: boolean;
  setIsPollingTimedOut: React.Dispatch<React.SetStateAction<boolean>>;
  similarityChecksDataMap: SimilarityChecksDataMapType;
  setSimilarityChecksDataMap: React.Dispatch<React.SetStateAction<SimilarityChecksDataMapType>>;
  submitForCheckErrorCodeMap: SubmitForCheckErrorMapType;
  setSubmitForCheckErrorCodeMap: React.Dispatch<React.SetStateAction<SubmitForCheckErrorMapType>>;

  hasContentMap: HasContentMapType;
  setHasContentMap: React.Dispatch<React.SetStateAction<HasContentMapType>>;
};

const PlagiarismDetectionContext = createContext<PlagiarismDetectionContextType>({
  isReadyForCheck: false,
  setIsReadyForCheck: () => undefined,
  isPollingTimedOut: false,
  setIsPollingTimedOut: () => undefined,
  similarityChecksDataMap: {},
  setSimilarityChecksDataMap: () => undefined,
  submitForCheckErrorCodeMap: {},
  setSubmitForCheckErrorCodeMap: () => undefined,

  hasContentMap: {},
  setHasContentMap: () => undefined,
});

export const usePlagiarismDetectionContext = () => {
  return useContext(PlagiarismDetectionContext);
};

/**
 * This component manages the ready for check context and the overall result data
 * it should be placed on the assignment level component
 */
export const PlagiarismDetectionContextProvider: React.LegacyFunctionComponentWithChildren<{}> = ({ children }) => {
  const [isReadyForCheck, setIsReadyForCheck] = useState(false);
  const [isPollingTimedOut, setIsPollingTimedOut] = useState(false);
  const [similarityChecksDataMap, setSimilarityChecksDataMap] = useState({});
  const [submitForCheckErrorCodeMap, setSubmitForCheckErrorCodeMap] = useState({});
  const [hasContentMap, setHasContentMap] = useState({});

  return (
    <PlagiarismDetectionContext.Provider
      value={{
        isReadyForCheck,
        setIsReadyForCheck,
        isPollingTimedOut,
        setIsPollingTimedOut,
        similarityChecksDataMap,
        setSimilarityChecksDataMap,
        submitForCheckErrorCodeMap,
        setSubmitForCheckErrorCodeMap,
        hasContentMap,
        setHasContentMap,
      }}
    >
      {children}
    </PlagiarismDetectionContext.Provider>
  );
};
